import { c as _c } from "react-compiler-runtime";
import React, { useEffect, useMemo, useRef } from 'react';
import { RemoteComponent } from '@nike/remote-component';
import useI18nString from '@nike/shop-apis/assets/i18n/dotcom-cart/use-query';
import useFetchStoreLocations from '@nike/shop-apis/store/store_locations/v1/use-query';
import selectStoreLocationsWithEnabledBopisService from '@nike/shop-apis/store/store_locations/v1/select';
import useFetchPickupOfferings from '@nike/shop-apis/buy/fulfillment_offerings/v1/fetch-pickup-offering/use-queries';
import getWebShellClientLocale from '@nike/shop-apis/utils/get-webshellclient-locale';
import useFetchIdentityUserAddress from '@nike/shop-apis/identity/user/v1/address/use-query';
import { selectPreferredAddress } from '@nike/shop-apis/identity/user/v1/address/select';
import useRetailMFEStore from '@nike/shop-apis/store/retail-mfe/use-store';
import pickupDayOrDate from '@nike/shop-apis/utils/date/pickup-day-or-date';
import { RemoteComponentWrapper } from './styles';
import useShopDialogStore from "../../../store/shop-dialog/use-store";
import { PICKUP_OFFERINGS_VIEWED } from "../../../analytics/constants";
import { LoadingIcon } from "../LoadingIndicator";
import trackEvent from "../../../analytics/track-event";
var formatStoreFromPickupOffering = function formatStoreFromPickupOffering(_ref) {
  var _queryResult$data, _queryResult$data2, _pickupItem$fulfillme, _getBy$minDate, _getBy$maxDate, _getBy$maxDate2;
  var queryResult = _ref.queryResult,
    storeLocation = _ref.storeLocation,
    translations = _ref.translations,
    language = _ref.language;
  var hasAvailability = (queryResult === null || queryResult === void 0 ? void 0 : queryResult.isSuccess) && (queryResult === null || queryResult === void 0 || (_queryResult$data = queryResult.data) === null || _queryResult$data === void 0 || (_queryResult$data = _queryResult$data.items) === null || _queryResult$data === void 0 ? void 0 : _queryResult$data.length) > 0;
  var pickupItem = queryResult === null || queryResult === void 0 || (_queryResult$data2 = queryResult.data) === null || _queryResult$data2 === void 0 || (_queryResult$data2 = _queryResult$data2.items) === null || _queryResult$data2 === void 0 ? void 0 : _queryResult$data2[0];
  var getBy = pickupItem === null || pickupItem === void 0 || (_pickupItem$fulfillme = pickupItem.fulfillmentOfferings) === null || _pickupItem$fulfillme === void 0 || (_pickupItem$fulfillme = _pickupItem$fulfillme[0]) === null || _pickupItem$fulfillme === void 0 ? void 0 : _pickupItem$fulfillme.getBy;
  var availableMessage = translations.availableMessage,
    pickupTodayMessage = translations.pickupTodayMessage,
    pickupTomorrowMessage = translations.pickupTomorrowMessage,
    pickupUnavailableMessage = translations.pickupUnavailableMessage;
  var minPickupDate = getBy === null || getBy === void 0 || (_getBy$minDate = getBy.minDate) === null || _getBy$minDate === void 0 ? void 0 : _getBy$minDate.dateTime;
  var maxPickupDate = getBy === null || getBy === void 0 || (_getBy$maxDate = getBy.maxDate) === null || _getBy$maxDate === void 0 ? void 0 : _getBy$maxDate.dateTime;
  var timeZone = getBy === null || getBy === void 0 || (_getBy$maxDate2 = getBy.maxDate) === null || _getBy$maxDate2 === void 0 ? void 0 : _getBy$maxDate2.timezone;
  var finalDate = pickupDayOrDate({
    language: language,
    pickupTodayMessage: pickupTodayMessage,
    pickupTomorrowMessage: pickupTomorrowMessage,
    minPickupDate: minPickupDate,
    maxPickupDate: maxPickupDate,
    timeZone: timeZone
  });
  if (hasAvailability) {
    return {
      disabled: false,
      storeId: storeLocation.id,
      getBy: getBy,
      availabilityMessage: "".concat(availableMessage, " ").concat(finalDate)
    };
  } else {
    return {
      disabled: true,
      storeId: storeLocation.id,
      availabilityMessage: pickupUnavailableMessage
    };
  }
};

/*
 * BOPISContent displays a list of Nike stores with availability of an individual sku at each
 * In order to determine the list of stores displayed, it follows a specific preference of location info, choosing the first option that is available
 * 1. Coordinates from local storage from the LS MFE
 * 2. Postal code from Identity API, if it's in the same country as the nike.com page
 * 3. SS MFE's internal store list - https://nikedigital.slack.com/archives/C01E7J6EDLK/p1754603862211299?thread_ts=1754597759.283669&cid=C01E7J6EDLK
 *   1. Coordinates from geoloc cookie if browser's geolocation permissions are granted
 *   2. Hard-coded coordinates from the largest city in the nike.com page's current country
 *   3. NYC House of Innovation Nike Store's coordinates
 */
var BOPISContent = function BOPISContent() {
  var $ = _c(47);
  var t0;
  if ($[0] === Symbol["for"]("react.memo_cache_sentinel")) {
    t0 = getWebShellClientLocale();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  var _t = t0,
    country = _t.country,
    language = _t.hreflang;
  var skuId = useShopDialogStore(_temp);
  var hasTrackedEvent = useRef(false);
  var _useRetailMFEStore = useRetailMFEStore(_temp2),
    latitude_0 = _useRetailMFEStore.latitude,
    longitude_0 = _useRetailMFEStore.longitude,
    setLocationSearch_0 = _useRetailMFEStore.setLocationSearch,
    setSelectedStore_0 = _useRetailMFEStore.setSelectedStore;
  var t1;
  if ($[1] === Symbol["for"]("react.memo_cache_sentinel")) {
    t1 = {
      stringKey: "pickupTodayMessage",
      primaryValue: "Today",
      description: "Today message for pickup"
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  var pickupTodayMessage = useI18nString(t1);
  var t2;
  if ($[2] === Symbol["for"]("react.memo_cache_sentinel")) {
    t2 = {
      stringKey: "pickupTomorrowMessage",
      primaryValue: "Tomorrow",
      description: "Tomorrow message for pickup"
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  var pickupTomorrowMessage = useI18nString(t2);
  var t3;
  if ($[3] === Symbol["for"]("react.memo_cache_sentinel")) {
    t3 = {
      stringKey: "pickupUnavailableMessage",
      primaryValue: "Unavailable",
      description: "Unavailable in store for pickup message"
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  var pickupUnavailableMessage = useI18nString(t3);
  var t4;
  if ($[4] === Symbol["for"]("react.memo_cache_sentinel")) {
    t4 = {
      stringKey: "availableMessage",
      primaryValue: "Available",
      description: "Indicates availability"
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  var availableMessage = useI18nString(t4);
  var t5;
  if ($[5] !== availableMessage || $[6] !== pickupTodayMessage || $[7] !== pickupTomorrowMessage || $[8] !== pickupUnavailableMessage) {
    t5 = {
      pickupTodayMessage: pickupTodayMessage,
      pickupTomorrowMessage: pickupTomorrowMessage,
      pickupUnavailableMessage: pickupUnavailableMessage,
      availableMessage: availableMessage
    };
    $[5] = availableMessage;
    $[6] = pickupTodayMessage;
    $[7] = pickupTomorrowMessage;
    $[8] = pickupUnavailableMessage;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  var translations = t5;
  var t6 = !(longitude_0 && latitude_0);
  var t7;
  if ($[10] !== t6) {
    t7 = {
      select: selectPreferredAddress,
      enabled: t6
    };
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  var _useFetchIdentityUser = useFetchIdentityUserAddress(t7),
    t8 = _useFetchIdentityUser.data,
    isIdentityAddressFetching = _useFetchIdentityUser.isFetching;
  var t9;
  if ($[12] !== t8) {
    t9 = t8 === undefined ? {
      code: ""
    } : t8;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  var _t2 = t9,
    identityUserPostalCode = _t2.code;
  var t10;
  if ($[14] !== identityUserPostalCode || $[15] !== latitude_0 || $[16] !== longitude_0) {
    t10 = {
      anchor: 0,
      count: 25,
      longitude: longitude_0,
      latitude: latitude_0,
      postalCode: identityUserPostalCode,
      select: selectStoreLocationsWithEnabledBopisService
    };
    $[14] = identityUserPostalCode;
    $[15] = latitude_0;
    $[16] = longitude_0;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  var _useFetchStoreLocatio = useFetchStoreLocations(t10),
    storeLocations = _useFetchStoreLocatio.data,
    isStoreLocationsFetching = _useFetchStoreLocatio.isFetching;
  var t11;
  if ($[18] !== skuId || $[19] !== storeLocations) {
    t11 = {
      skuId: skuId,
      storeLocations: storeLocations
    };
    $[18] = skuId;
    $[19] = storeLocations;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  var pickupOfferingQueries = useFetchPickupOfferings(t11);
  var t12;
  if ($[21] !== pickupOfferingQueries) {
    t12 = pickupOfferingQueries === null || pickupOfferingQueries === void 0 ? void 0 : pickupOfferingQueries.some(_temp3);
    $[21] = pickupOfferingQueries;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  var isPickupOfferingsFetching = t12;
  var t13;
  if ($[23] !== pickupOfferingQueries || $[24] !== storeLocations || $[25] !== translations) {
    t13 = pickupOfferingQueries && storeLocations !== null && storeLocations !== void 0 && storeLocations.objects ? pickupOfferingQueries === null || pickupOfferingQueries === void 0 ? void 0 : pickupOfferingQueries.map(function (queryResult_0, index) {
      return formatStoreFromPickupOffering({
        queryResult: queryResult_0,
        storeLocation: storeLocations.objects[index],
        translations: translations,
        language: language
      });
    }) : [];
    $[23] = pickupOfferingQueries;
    $[24] = storeLocations;
    $[25] = translations;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  var stores = t13;
  var t14;
  var t15;
  if ($[27] !== isIdentityAddressFetching || $[28] !== isPickupOfferingsFetching || $[29] !== isStoreLocationsFetching || $[30] !== latitude_0 || $[31] !== longitude_0 || $[32] !== stores) {
    t14 = function t14() {
      if (hasTrackedEvent.current) {
        return;
      }
      if (isStoreLocationsFetching || isPickupOfferingsFetching || isIdentityAddressFetching) {
        return;
      }
      if (stores.length === 0 && !(latitude_0 && longitude_0)) {
        return;
      }
      var pickupOfferingStatus;
      if (stores.some(_temp4)) {
        pickupOfferingStatus = "offerings";
      } else {
        if (stores.length > 0) {
          pickupOfferingStatus = "stores but no offerings";
        } else {
          pickupOfferingStatus = "no stores";
        }
      }
      trackEvent(PICKUP_OFFERINGS_VIEWED, {
        fulfillment: {
          pickupOfferingStatus: pickupOfferingStatus
        }
      }, {
        usePageAction: true
      });
      hasTrackedEvent.current = true;
    };
    t15 = [isStoreLocationsFetching, isPickupOfferingsFetching, isIdentityAddressFetching, stores, latitude_0, longitude_0];
    $[27] = isIdentityAddressFetching;
    $[28] = isPickupOfferingsFetching;
    $[29] = isStoreLocationsFetching;
    $[30] = latitude_0;
    $[31] = longitude_0;
    $[32] = stores;
    $[33] = t14;
    $[34] = t15;
  } else {
    t14 = $[33];
    t15 = $[34];
  }
  useEffect(t14, t15);
  var t16;
  if ($[35] !== setLocationSearch_0) {
    t16 = function t16(args) {
      setLocationSearch_0(args);
    };
    $[35] = setLocationSearch_0;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  var t17;
  if ($[37] !== stores) {
    t17 = {
      searchType: "SPECIFIED",
      stores: stores
    };
    $[37] = stores;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  var t18 = isStoreLocationsFetching || isPickupOfferingsFetching;
  var t19 = !(isStoreLocationsFetching || isPickupOfferingsFetching || isIdentityAddressFetching) && stores.length === 0 ? "searchNearestCity" : "message";
  var t20;
  if ($[39] !== setSelectedStore_0) {
    t20 = function t20(store_0) {
      setSelectedStore_0(store_0);
    };
    $[39] = setSelectedStore_0;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  var t21;
  if ($[41] !== t16 || $[42] !== t17 || $[43] !== t18 || $[44] !== t19 || $[45] !== t20) {
    t21 = /*#__PURE__*/React.createElement(RemoteComponentWrapper, null, /*#__PURE__*/React.createElement(RemoteComponent, {
      endpoint: "https://www.nike.com/fragments/store-selector",
      prefetch: false,
      FallbackComponent: LoadingIcon,
      onDetailedSubmit: t16,
      config: t17,
      isLoading: t18,
      limitToCountries: country,
      coldState: t19,
      onStoreSelect: t20
    }));
    $[41] = t16;
    $[42] = t17;
    $[43] = t18;
    $[44] = t19;
    $[45] = t20;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  return t21;
};
export default BOPISContent;
function _temp(state) {
  return state.skuId;
}
function _temp2(t0) {
  var t1 = t0.locationSearch,
    setLocationSearch = t0.setLocationSearch,
    setSelectedStore = t0.setSelectedStore;
  var t2 = t1.coordinates;
  var latitude = t2.latitude,
    longitude = t2.longitude;
  return {
    latitude: latitude,
    longitude: longitude,
    setLocationSearch: setLocationSearch,
    setSelectedStore: setSelectedStore
  };
}
function _temp3(queryResult) {
  return queryResult.isFetching;
}
function _temp4(store) {
  return !store.disabled;
}